"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Full-screen push-over overlay menu — React port of the codegrid
 * push-over-overlay-menu. Pushes the main content DOWN by 100svh while
 * the overlay clipPaths in from the top, then the internal menu copy
 * slides up line-by-line. Opens via the `bo:open-menu` event
 * (dispatched by the TopNav hamburger) so no menu-bar is rendered.
 */
const PRIMARY_LINKS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Books", href: "/books" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

const SOCIALS = [
  { label: "Instagram", href: "https://instagram.com/burakozgurmd" },
  { label: "Facebook", href: "https://facebook.com/BurakOzgurMD" },
  { label: "TikTok", href: "https://tiktok.com/@burak.ozgur.md" },
  { label: "YouTube", href: "https://www.youtube.com/@burakozgurmd" },
];

export default function PushOverMenu() {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const mediaRef = useRef(null);
  const linesRef = useRef([]);
  const [isOpen, setIsOpen] = useState(false);
  const isAnimatingRef = useRef(false);
  const pathname = usePathname();

  // Collect every line-wrapper once on mount.
  useEffect(() => {
    gsap.registerPlugin(CustomEase);
    if (!CustomEase.get("hop")) CustomEase.create("hop", ".87,0,.13,1");

    if (overlayRef.current) {
      linesRef.current = Array.from(
        overlayRef.current.querySelectorAll(".pom-line")
      );
      gsap.set(linesRef.current, { y: "-110%" });
    }
  }, []);

  // Control the <main> push + overlay animation.
  const openMenu = () => {
    if (isAnimatingRef.current || isOpen) return;
    isAnimatingRef.current = true;

    const main = document.querySelector("main");
    const overlay = overlayRef.current;
    const content = contentRef.current;
    const media = mediaRef.current;

    document.body.style.overflow = "hidden";

    const tl = gsap.timeline({
      onComplete: () => {
        isAnimatingRef.current = false;
      },
    });

    if (main) {
      tl.to(main, { y: "100svh", duration: 1, ease: "hop" }, 0);
    }
    tl.to(
      overlay,
      {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
        duration: 1,
        ease: "hop",
      },
      0
    )
      .to(content, { yPercent: 0, duration: 1, ease: "hop" }, 0)
      .to(media, { opacity: 1, duration: 0.75, ease: "power2.out", delay: 0.5 }, 0)
      .to(
        linesRef.current,
        {
          y: "0%",
          duration: 1.6,
          ease: "hop",
          stagger: -0.06,
        },
        0.1
      );

    setIsOpen(true);
  };

  const closeMenu = () => {
    if (isAnimatingRef.current || !isOpen) return;
    isAnimatingRef.current = true;

    const main = document.querySelector("main");
    const overlay = overlayRef.current;
    const content = contentRef.current;
    const media = mediaRef.current;

    const tl = gsap.timeline({
      onComplete: () => {
        // Reset state for next open.
        gsap.set(linesRef.current, { y: "-110%" });
        gsap.set(media, { opacity: 0 });
        // Strip the transform off <main> entirely. A lingering
        // `transform: translate3d(0,0,0)` re-roots position:fixed
        // descendants to <main> instead of the viewport, which breaks
        // ScrollTrigger's pinned sections (MaximaTherapy / Waabi /
        // StickyCards all go white after a menu close).
        if (main) gsap.set(main, { clearProps: "transform,translate,y" });
        isAnimatingRef.current = false;
        document.body.style.overflow = "";
        // Re-measure pins now that main is untransformed. Safe no-op
        // when ScrollTrigger has no active triggers.
        try {
          ScrollTrigger.refresh();
        } catch {}
      },
    });

    if (main) {
      tl.to(main, { y: "0svh", duration: 1, ease: "hop" }, 0);
    }
    tl.to(
      overlay,
      {
        clipPath: "polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)",
        duration: 1,
        ease: "hop",
      },
      0
    ).to(content, { yPercent: -50, duration: 1, ease: "hop" }, 0);

    setIsOpen(false);
  };

  // Listen for the TopNav hamburger.
  useEffect(() => {
    const onOpen = () => openMenu();
    window.addEventListener("bo:open-menu", onOpen);
    return () => window.removeEventListener("bo:open-menu", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close on ESC.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && isOpen) closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-close when the route changes (user clicked a link inside menu).
  const lastPathRef = useRef(pathname);
  useEffect(() => {
    if (pathname !== lastPathRef.current) {
      lastPathRef.current = pathname;
      if (isOpen) closeMenu();
      // Defensive: force-reset main's transform + body overflow after
      // the close window has elapsed, even if the close animation was
      // interrupted. Otherwise a stuck translate keeps fixed children
      // (ScrollTrigger pins) re-rooted to main and the page goes
      // white below the fold.
      setTimeout(() => {
        const main = document.querySelector("main");
        if (main) gsap.set(main, { clearProps: "transform,translate,y" });
        document.body.style.overflow = "";
        try {
          ScrollTrigger.refresh();
        } catch {}
      }, 1100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Each text element is wrapped in <span class="pom-line"> so the
  // overflow-hidden parent clips the pre-set y: -110% offset cleanly.
  const Line = ({ children, className = "" }) => (
    <div className={`pom-line-wrap ${className}`}>
      <span className="pom-line">{children}</span>
    </div>
  );

  return (
    <div
      ref={overlayRef}
      className={`pom-overlay${isOpen ? " is-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div ref={contentRef} className="pom-content">
        <div ref={mediaRef} className="pom-media">
          <img
            src="https://for-media.b-cdn.net/ozgurmd/burak-ozgur-headshot-2.JPG"
            alt=""
          />
        </div>

        <div className="pom-right">
          <button
            type="button"
            className="pom-close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            Close
          </button>

          <div className="pom-main">
            <div className="pom-col pom-col-primary">
              {PRIMARY_LINKS.map((link) => (
                <div key={link.href} className="pom-link">
                  <Line>
                    <a href={link.href}>{link.label}</a>
                  </Line>
                </div>
              ))}
            </div>
            <div className="pom-col pom-col-tags">
              <div className="pom-social-heading">
                <Line>
                  <span>Follow Burak</span>
                </Line>
              </div>
              {SOCIALS.map((social) => (
                <div key={social.label} className="pom-tag">
                  <Line>
                    <a href={social.href} target="_blank" rel="noreferrer">
                      {social.label}
                    </a>
                  </Line>
                </div>
              ))}
            </div>
          </div>

          <div className="pom-footer">
            <div className="pom-col">
              <Line>
                <p>Newport Beach, CA</p>
              </Line>
            </div>
            <div className="pom-col">
              <Line>
                <p>
                  <a href="mailto:contact@ozgurmd.com">contact@ozgurmd.com</a>
                </p>
              </Line>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
